// /festive-capacity — Festive Capacity Monitor (PAGES.md §3.89)
//
// Real-time capacity monitoring for festive order periods. Max orders/day config,
// current booking count, kitchen load %, auto-stop status. Capacity by day
// across the festival period with heatmap visualization.
// RESTAURANT vertical only — MANAGER+ (level 30). Feature flag: ENABLE_FESTIVE_MODULE.
// Traceability: FR-FEST-010, FR-FEST-011, FR-FEST-012

import { useState, useCallback, useEffect, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  PageContainer,
  Button,
  Badge,
  Select,
  Input,
  Skeleton,
  EmptyState,
  ErrorBoundary,
  StatCard,
  DataTable,
  Toggle,
  HeatmapChart,
  useToast,
} from '@bbpos/ui';
import type { ColumnDef } from '@bbpos/ui';
import { festiveService } from '@/services/festive.service';
import type { Festival, CapacityDay } from '@/services/festive.service';
import { useAuthStore } from '@/stores/use-auth-store';
import { GLASS, GLASS_FILTER, GLASS_INTERACTIVE, cn } from '@/constants/design-tokens';
import { ApiError } from '@/lib/api-client';
import { ROLE_LEVELS, CAPACITY_BADGE, formatDate } from './constants';
import {
  UsersIcon, ActivityIcon, BarChartIcon,
  SettingsIcon, ZapIcon, CalendarEmptyIcon,
} from './icons';

// ─── Mobile Capacity Card ───────────────────────────────────────────────────

function CapacityMobileCard({ day }: { day: CapacityDay }) {
  const badge = CAPACITY_BADGE[day.status] ?? CAPACITY_BADGE.OPEN;
  const fillPct = day.max_orders > 0 ? Math.round((day.booked_orders / day.max_orders) * 100) : 0;
  return (
    <div className={cn(GLASS_INTERACTIVE, 'p-4 animate-slide-up')}>
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0 flex-1">
          <p className="font-bold text-neutral-900 dark:text-neutral-100 font-mono tabular-nums">
            {formatDate(day.date)}
          </p>
          <p className="text-xs text-neutral-500 dark:text-neutral-400 mt-0.5">
            {day.booked_orders} / {day.max_orders} orders
          </p>
        </div>
        <Badge variant={badge.variant} size="sm">{badge.label}</Badge>
      </div>
      {/* Progress bar */}
      <div className="mt-3 h-2 rounded-full bg-neutral-200 dark:bg-neutral-700 overflow-hidden">
        <div
          className={cn(
            'h-full rounded-full transition-all duration-300',
            fillPct >= 100 ? 'bg-rose-500' : fillPct >= 80 ? 'bg-amber-500' : 'bg-emerald-500',
          )}
          style={{ width: `${Math.min(fillPct, 100)}%` }}
        />
      </div>
      <div className="flex items-center justify-between mt-2">
        <span className="text-xs font-mono tabular-nums text-neutral-500 dark:text-neutral-400">
          {fillPct}% filled
        </span>
        <span className="text-xs font-mono tabular-nums text-neutral-500 dark:text-neutral-400">
          Kitchen: {day.kitchen_load_pct}%
        </span>
      </div>
    </div>
  );
}

// ─── Component ──────────────────────────────────────────────────────────────

function FestiveCapacityContent() {
  const { toast } = useToast();
  const profile = useAuthStore((s) => s.profile);
  const roleLevel = ROLE_LEVELS[profile?.role ?? ''] ?? 0;
  const canEdit = roleLevel >= 50; // OWNER+

  const [searchParams, setSearchParams] = useSearchParams();
  const selectedEventId = searchParams.get('event') ?? '';

  const [festivals, setFestivals] = useState<Festival[]>([]);
  const [capacityDays, setCapacityDays] = useState<CapacityDay[]>([]);
  const [isLoading, setIsLoading] = useState(false);

  // ── Settings ────────────────────────────────────────────────────────────
  const [maxOrdersPerDay, setMaxOrdersPerDay] = useState('');
  const [autoStopEnabled, setAutoStopEnabled] = useState(true);
  const [isSaving, setIsSaving] = useState(false);

  useEffect(() => { document.title = 'Festive Capacity Monitor — Orio360 Admin'; }, []);

  // Fetch festivals
  useEffect(() => {
    festiveService.listFestivals().then((r) => setFestivals(r.data)).catch(() => { /* silent — festival selector stays empty */ });
  }, []);

  const festivalOptions = useMemo(() =>
    festivals.map((f) => ({ value: f.id, label: f.name })),
  [festivals]);

  // Fetch capacity
  const fetchCapacity = useCallback(async () => {
    if (!selectedEventId) return;
    setIsLoading(true);
    try {
      const data = await festiveService.getCapacity(selectedEventId);
      setCapacityDays(data);
      if (data.length > 0) {
        setMaxOrdersPerDay(String(data[0].max_orders));
      }
    } catch (err) {
      toast({ title: err instanceof ApiError ? err.message : 'Failed to load capacity.', variant: 'error' });
    } finally {
      setIsLoading(false);
    }
  }, [selectedEventId, toast]);

  useEffect(() => { fetchCapacity(); }, [fetchCapacity]);

  // ── Save settings ─────────────────────────────────────────────────────
  const handleSave = useCallback(async () => {
    if (!selectedEventId) return;
    setIsSaving(true);
    try {
      await festiveService.updateCapacity(selectedEventId, {
        max_orders_per_day: parseInt(maxOrdersPerDay, 10) || 0,
        auto_stop_enabled: autoStopEnabled,
      });
      toast({ title: 'Capacity settings saved', variant: 'success' });
      fetchCapacity();
    } catch (err) {
      toast({ title: err instanceof ApiError ? err.message : 'Failed to save.', variant: 'error' });
    } finally {
      setIsSaving(false);
    }
  }, [selectedEventId, maxOrdersPerDay, autoStopEnabled, fetchCapacity, toast]);

  // ── Keyboard shortcut: Ctrl+S to save (spec §3.89) ────────────────────
  useEffect(() => {
    function handleKeyDown(e: KeyboardEvent) {
      if (e.key === 's' && (e.ctrlKey || e.metaKey) && selectedEventId && canEdit) {
        e.preventDefault();
        handleSave();
      }
    }
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [selectedEventId, canEdit, handleSave]);

  // ── Override auto-stop ────────────────────────────────────────────────
  const handleOverride = useCallback(async () => {
    if (!selectedEventId) return;
    try {
      await festiveService.overrideAutoStop(selectedEventId);
      toast({ title: 'Auto-stop overridden — bookings resumed', variant: 'success' });
      fetchCapacity();
    } catch (err) {
      toast({ title: err instanceof ApiError ? err.message : 'Cannot override — kitchen load at maximum', variant: 'error' });
    }
  }, [selectedEventId, fetchCapacity, toast]);

  // ── Derived stats ─────────────────────────────────────────────────────
  const todayData = capacityDays.find((d) => d.date === new Date().toISOString().split('T')[0]);
  const avgKitchenLoad = capacityDays.length > 0
    ? Math.round(capacityDays.reduce((s, d) => s + d.kitchen_load_pct, 0) / capacityDays.length)
    : 0;
  const fullDays = capacityDays.filter((d) => d.status === 'FULL' || d.status === 'AUTO_STOPPED').length;

  // ── Heatmap data ──────────────────────────────────────────────────────
  const heatmapData = useMemo(() =>
    capacityDays.map((d) => ({
      row: formatDate(d.date),
      col: 'Capacity',
      value: d.max_orders > 0 ? Math.round((d.booked_orders / d.max_orders) * 100) : 0,
    })),
  [capacityDays]);

  // ── Table columns ─────────────────────────────────────────────────────
  const columns: ColumnDef<CapacityDay>[] = useMemo(() => [
    {
      key: 'date', header: 'Date',
      render: (_v: unknown, row: CapacityDay) => (
        <span className="font-mono tabular-nums text-neutral-900 dark:text-neutral-100">{formatDate(row.date)}</span>
      ),
    },
    {
      key: 'max_orders', header: 'Max Orders', numeric: true,
      render: (_v: unknown, row: CapacityDay) => (
        <span className="font-mono tabular-nums">{row.max_orders.toLocaleString('en-IN')}</span>
      ),
    },
    {
      key: 'booked_orders', header: 'Booked', numeric: true,
      render: (_v: unknown, row: CapacityDay) => (
        <span className="font-mono tabular-nums font-bold">{row.booked_orders.toLocaleString('en-IN')}</span>
      ),
    },
    {
      key: 'remaining', header: 'Remaining', numeric: true,
      render: (_v: unknown, row: CapacityDay) => (
        <span className={cn('font-mono tabular-nums font-bold', row.remaining <= 0 ? 'text-danger-600' : 'text-success-600')}>
          {row.remaining.toLocaleString('en-IN')}
        </span>
      ),
    },
    {
      key: 'kitchen_load_pct', header: 'Kitchen Load', numeric: true,
      render: (_v: unknown, row: CapacityDay) => (
        <span className={cn('font-mono tabular-nums', row.kitchen_load_pct >= 80 ? 'text-danger-600 font-bold' : '')}>
          {row.kitchen_load_pct}%
        </span>
      ),
    },
    {
      key: 'status', header: 'Status',
      render: (_v: unknown, row: CapacityDay) => {
        const badge = CAPACITY_BADGE[row.status] ?? CAPACITY_BADGE.OPEN;
        return <Badge variant={badge.variant} size="sm">{badge.label}</Badge>;
      },
    },
  ], []);

  // ── Capacity alert banners ────────────────────────────────────────────
  const highCapacityDays = capacityDays.filter((d) => {
    const pct = d.max_orders > 0 ? (d.booked_orders / d.max_orders) * 100 : 0;
    return pct >= 80 && d.status !== 'FULL' && d.status !== 'AUTO_STOPPED';
  });
  const stoppedDays = capacityDays.filter((d) => d.status === 'AUTO_STOPPED');

  return (
    <PageContainer
      title="Festive Capacity Monitor"
      subtitle="Track and manage booking capacity for festive events"
      breadcrumbs={[
        { label: 'Home', href: '/home' },
        { label: 'Festive Capacity Monitor' },
      ]}
      actions={
        canEdit ? (
          <div className="flex items-center gap-3">
            {stoppedDays.length > 0 && (
              <Button variant="danger" size="sm" onClick={handleOverride}>Override Auto-Stop</Button>
            )}
            <Button variant="primary" size="sm" onClick={handleSave} disabled={isSaving || !selectedEventId}>
              <SettingsIcon className="h-4 w-4" /><span className="ml-1.5">{isSaving ? 'Saving...' : 'Save Settings'}</span>
            </Button>
          </div>
        ) : undefined
      }
    >
      <div className="animate-fade-in">
        {/* ═══ Event Selector ═══ */}
        <div className={cn(GLASS_FILTER, 'p-4 mb-6')}>
          <div className="flex flex-wrap items-center gap-4">
            <div className="w-56">
              <Select
                options={festivalOptions}
                value={selectedEventId}
                onChange={(val) => {
                  const v = typeof val === 'string' ? val : val[0] ?? '';
                  setSearchParams(v ? { event: v } : {}, { replace: true });
                }}
                placeholder="Select Festival"
              />
            </div>
            {canEdit && selectedEventId && (
              <div className="flex items-center gap-4 ml-auto">
                <div className="flex items-center gap-2">
                  <label className="text-xs font-medium text-neutral-500 dark:text-neutral-400">Max / Day</label>
                  <Input
                    type="number"
                    value={maxOrdersPerDay}
                    onChange={(val) => setMaxOrdersPerDay(val)}
                    className="w-24"
                  />
                </div>
                <div className="flex items-center gap-2">
                  <label className="text-xs font-medium text-neutral-500 dark:text-neutral-400">Auto-Stop</label>
                  <Toggle checked={autoStopEnabled} onChange={() => setAutoStopEnabled((p) => !p)} />
                </div>
              </div>
            )}
          </div>
        </div>

        {/* ═══ Alert banners ═══ */}
        {highCapacityDays.length > 0 && (
          <div className="bg-amber-50 dark:bg-amber-900/20 rounded-xl p-4 mb-4 text-amber-800 dark:text-amber-200 text-sm">
            <strong>80%+ booked:</strong> {highCapacityDays.map((d) => formatDate(d.date)).join(', ')}
          </div>
        )}
        {stoppedDays.length > 0 && (
          <div className="bg-rose-50 dark:bg-rose-900/20 rounded-xl p-4 mb-4 text-rose-800 dark:text-rose-200 text-sm">
            <strong>Capacity reached — bookings stopped:</strong> {stoppedDays.map((d) => formatDate(d.date)).join(', ')}
          </div>
        )}

        {!selectedEventId ? (
          <EmptyState
            variant="page"
            icon={<CalendarEmptyIcon />}
            title="No festival selected"
            description="Select a festival event to view capacity"
          />
        ) : isLoading ? (
          <div className="space-y-6">
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
              {[1, 2, 3, 4].map((i) => <StatCard key={i} title="" value="" loading />)}
            </div>
            <div className={cn(GLASS, 'p-6')}><Skeleton variant="rect" width="100%" height={200} /></div>
          </div>
        ) : (
          <>
            {/* ═══ KPI Cards ═══ */}
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
              <div className="animate-slide-up">
                <StatCard
                  title="Max Daily Capacity"
                  value={capacityDays[0]?.max_orders?.toLocaleString('en-IN') ?? '—'}
                  icon={<BarChartIcon className="h-4 w-4" />}
                />
              </div>
              <div className="animate-slide-up" style={{ animationDelay: '50ms' }}>
                <StatCard
                  title="Today's Bookings"
                  value={todayData ? `${todayData.booked_orders}/${todayData.max_orders}` : '—'}
                  icon={<UsersIcon className="h-4 w-4" />}
                />
              </div>
              <div className="animate-slide-up" style={{ animationDelay: '100ms' }}>
                <StatCard
                  title="Avg Kitchen Load"
                  value={`${avgKitchenLoad}%`}
                  icon={<ActivityIcon className="h-4 w-4" />}
                  variant={avgKitchenLoad > 80 ? 'trend' : 'default'}
                  trend={avgKitchenLoad > 80 ? { direction: 'up', value: `${avgKitchenLoad}%`, positive: false } : undefined}
                />
              </div>
              <div className="animate-slide-up" style={{ animationDelay: '150ms' }}>
                <StatCard
                  title="Full Days"
                  value={String(fullDays)}
                  icon={<ZapIcon className="h-4 w-4" />}
                />
              </div>
            </div>

            {/* ═══ Heatmap ═══ */}
            {heatmapData.length > 0 && (
              <div className={cn(GLASS, 'p-4 mb-6')}>
                <HeatmapChart
                  data={heatmapData}
                  title="Daily Capacity Fill %"
                  rowLabel="Date"
                  colLabel=""
                />
              </div>
            )}

            {/* ═══ Desktop DataTable ═══ */}
            <div className={cn(GLASS, 'overflow-hidden hidden sm:block mb-6')}>
              <div className="px-5 py-4">
                <h3 className="text-sm font-bold text-neutral-700 dark:text-neutral-300 uppercase tracking-wider">
                  Capacity by Day ({capacityDays.length})
                </h3>
              </div>
              <DataTable<CapacityDay & { id: string }> columns={columns as ColumnDef<CapacityDay & { id: string }>[]} data={capacityDays.map((d) => ({ ...d, id: d.date }))} />
            </div>

            {/* ═══ Mobile Cards ═══ */}
            <div className="block sm:hidden space-y-3 mb-6">
              <h3 className="text-sm font-bold text-neutral-700 dark:text-neutral-300 uppercase tracking-wider px-1">
                Capacity by Day ({capacityDays.length})
              </h3>
              {capacityDays.map((day) => (
                <CapacityMobileCard key={day.date} day={day} />
              ))}
            </div>
          </>
        )}
      </div>
    </PageContainer>
  );
}

export function FestiveCapacityPage() {
  return (
    <ErrorBoundary>
      <FestiveCapacityContent />
    </ErrorBoundary>
  );
}
